import { useParams } from "react-router-dom";
import { AlertTriangle, CheckCircle2, ClipboardList, Timer, UserCheck } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import FaqAccordion from "@/components/FaqAccordion";
import { useContent } from "@/lib/content";
import { useEnquiry } from "@/components/EnquiryModal";
import { IMG } from "@/lib/site";

export default function VisaDetail() {
  const { slug } = useParams();
  const { visas, loading } = useContent();
  const { openEnquiry } = useEnquiry();

  const visa = visas.find((v) => v.slug === slug);

  if (loading) return <div className="py-32 text-center text-slate-400">Loading visa guide...</div>;
  if (!visa) {
    return (
      <div className="py-32 text-center" data-testid="visa-not-found">
        <h1 className="font-heading text-3xl font-bold text-slate-900">Visa guide not found</h1>
      </div>
    );
  }

  return (
    <div data-testid={`visa-detail-${slug}`}>
      <SEO title={`${visa.country} Visa for Indians`} description={visa.overview} />
      <PageHero image={IMG.plane1} overline="Visa Assistance" title={`${visa.country} Visa`} description={visa.overview} />
      <section className="py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[1fr_340px] gap-10">
          <div className="space-y-8">
            <Reveal>
              <div className="rounded-2xl bg-white border border-slate-200/80 p-6 shadow-sm">
                <h2 className="font-heading text-xl font-bold text-slate-900 flex items-center gap-2"><UserCheck className="w-5 h-5 text-skybrand" /> Who Can Apply</h2>
                <ul className="mt-4 space-y-2">
                  {visa.who_can_apply.map((x) => (
                    <li key={x} className="flex items-start gap-2 text-sm text-slate-600"><CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /> {x}</li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal>
              <div className="rounded-2xl bg-white border border-slate-200/80 p-6 shadow-sm">
                <h2 className="font-heading text-xl font-bold text-slate-900 flex items-center gap-2"><ClipboardList className="w-5 h-5 text-skybrand" /> Documents Required</h2>
                <ul className="mt-4 grid sm:grid-cols-2 gap-2">
                  {visa.documents.map((x) => (
                    <li key={x} className="flex items-start gap-2 text-sm text-slate-600"><CheckCircle2 className="w-4 h-4 text-skybrand shrink-0 mt-0.5" /> {x}</li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal>
              <div className="rounded-2xl bg-white border border-slate-200/80 p-6 shadow-sm">
                <h2 className="font-heading text-xl font-bold text-slate-900 flex items-center gap-2"><Timer className="w-5 h-5 text-skybrand" /> Processing Information</h2>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{visa.processing}</p>
              </div>
            </Reveal>
            <Reveal>
              <div className="rounded-2xl bg-amber-50 border border-amber-200 p-6">
                <h2 className="font-heading text-xl font-bold text-slate-900 flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-gold" /> Important Notes</h2>
                <ul className="mt-3 space-y-2">
                  {visa.notes.map((n) => <li key={n} className="text-sm text-slate-600 leading-relaxed">• {n}</li>)}
                </ul>
              </div>
            </Reveal>
            <Reveal>
              <h2 className="font-heading text-xl font-bold text-slate-900">FAQs</h2>
              <div className="mt-3"><FaqAccordion faqs={visa.faqs} testidPrefix="visa-faq" /></div>
            </Reveal>
          </div>
          <aside>
            <div className="lg:sticky lg:top-28 rounded-2xl bg-navy p-7 text-white shadow-xl">
              <h3 className="font-heading text-xl font-bold">Apply with Expert Help</h3>
              <p className="mt-3 text-sm text-slate-300 leading-relaxed">
                Send us your travel dates and we will map the exact documents, timeline and fees for your {visa.country} visa.
              </p>
              <button onClick={() => openEnquiry({ destination: visa.country, tripType: "Visa Assistance", packageName: `${visa.country} Visa Assistance` })}
                      data-testid="visa-apply-button"
                      className="mt-6 w-full rounded-full bg-gold py-3.5 font-heading font-bold text-navydeep hover:bg-amber-400 transition-colors">
                APPLY / ENQUIRE NOW
              </button>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
